import { useAIAssessmentContext } from "../../../Context/AIAssessmentContext/AIAssessmentContext";
import { useLoader } from "../../../Context/LoaderContext/LoaderContext";
import aiInterviewService from "../../../Services/ai-interview.service";

interface UseAiAssessmentQuestionNavigationReturnType {
  navigateToNextQuestion: () => Promise<void>;
  navigateToPreviousQuestion: () => Promise<void>;
  saveTemporaryAnswer: (answer: string | null) => void;
  clearTemporaryAnswer: () => void;
  navigateToQuestion: (step: number) => Promise<void>;
}

const useAiAssessmentQuestionNavigation =
  (): UseAiAssessmentQuestionNavigationReturnType => {
    const {
      currentStep,
      currentSession,
      aiAssessmentQuestions,
      setAiAssessmentQuestions,
      temporaryAnswer,
      setTemporaryAnswer,
      setCurrentStep,
      isRoundFinished,
    } = useAIAssessmentContext();

    const { setLoading, setLoadingMessage, clearLoader } = useLoader();

    const saveTemporaryAnswer = (answer: string | null) => {
      setTemporaryAnswer(answer);
    };

    const clearTemporaryAnswer = () => {
      if (temporaryAnswer && temporaryAnswer.trim().length > 0) {
        setTemporaryAnswer(null);
      }
      return;
    };

    const handleAnswerSingleQuestion = async (
      step: number | null,
      type: "next" | "previous" | null
    ) => {

      const nextQuestionStep =
        typeof step === "number"
          ? step
          : type === "next"
          ? currentStep + 1
          : type === "previous"
          ? currentStep - 1
          : currentStep;

      // If the round is finished, navigate to the next question
      if (isRoundFinished) {
        setCurrentStep(nextQuestionStep);
        return;
      }

      // From the last screen navigate to the user clicked step
      if(currentStep === aiAssessmentQuestions.length){
        setCurrentStep(nextQuestionStep);
        return;
      }

      // If the question is not found, return
      const currentQuestion = aiAssessmentQuestions[currentStep];
      if (!currentQuestion || !currentSession) {
        console.warn(
          "useAiAssessmentQuestionNavigation: No question or session found"
        );
        return;
      }

      // The question is already answered, navigate to the next question
      const isAnswered =
        currentQuestion.answer !== null &&
        currentQuestion.answer !== undefined &&
        currentQuestion.answer.trim().length > 0;
      if (isAnswered) {
        setCurrentStep(nextQuestionStep);
        return;
      }

      const isAnswerEntered =
        temporaryAnswer !== null &&
        temporaryAnswer !== undefined &&
        temporaryAnswer.trim().length > 0;
      const sessionToken = localStorage.getItem("sessionToken");

      if (isAnswerEntered && sessionToken) {
        try {
          setLoading(true);
          setLoadingMessage("Submitting answer please wait...");

          const updatedQuestion = await aiInterviewService.answerSingleQuestion(
            temporaryAnswer,
            currentSession._id,
            currentQuestion.questionId,
            sessionToken
          );

          // TODO: Handle the error case
          if (!updatedQuestion) {
            console.error(
              "useAiAssessmentQuestionNavigation: No updated question found"
            );
            return;
          }

          setAiAssessmentQuestions((prev) => {
            const newQuestions = [...prev];
            newQuestions[currentStep] = {
              ...currentQuestion,
              answer: temporaryAnswer,
            };
            return newQuestions;
          });
          setCurrentStep(nextQuestionStep);
        } catch (error: any) {
          console.error(
            "useAiAssessmentQuestionNavigation: Error answering question",
            error
          );
        } finally {
          clearLoader();
        }
      } else {
        setCurrentStep(nextQuestionStep);
        return;
      }
    };

    const navigateToNextQuestion = async () => {
      if (currentStep === aiAssessmentQuestions.length) {
        console.warn(
          "useAiAssessmentQuestionNavigation: Cannot navigate to next question from last question"
        );
        return;
      }

      await handleAnswerSingleQuestion(currentStep + 1, "next");
    };

    const navigateToPreviousQuestion = async () => {
      if (currentStep === 0 || currentStep === -1) {
        console.warn(
          "useAiAssessmentQuestionNavigation: Cannot navigate back from first question"
        );
        return;
      } else if (currentStep === aiAssessmentQuestions.length) {
        setCurrentStep(currentStep - 1);
        return;
      } else {
        await handleAnswerSingleQuestion(currentStep - 1, "previous");
        return;
      }
    };

    const handleQuestionNavigationWithStep = async (step: number) => {
      if (step === -1) {
        console.warn(
          "useAiAssessmentQuestionNavigation: Cannot navigate to start question"
        );
        return;
      } else if (step === currentStep) {
        console.warn(
          "useAiAssessmentQuestionNavigation: Cannot navigate to same question"
        );
        return;
      } else {
        await handleAnswerSingleQuestion(step, null);
      }
    };

    const navigateToQuestion = async (step: number) => {
      if (step === -1) {
        console.warn(
          "useAiAssessmentQuestionNavigation: Cannot navigate to start question"
        );
        return;
      }
      await handleQuestionNavigationWithStep(step);
    };

    return {
      navigateToNextQuestion,
      navigateToPreviousQuestion,
      saveTemporaryAnswer,
      navigateToQuestion,
      clearTemporaryAnswer,
    };
  };

export default useAiAssessmentQuestionNavigation;
